// French (vous) copy for /web-uploader, translated from copy.en.ts. Same shape (checked by copy.test.ts).
// "L'outil d'envoi web" for the web uploader; "aperçus" and "originaux". French typography: a non-breaking
// space before : ; ? ! and inside « », and in 10 000.
// The uploader's own labels are in English, so they're quoted as it shows them (« Previews », « Originals »).
// The text inside the two animations stays in English, like the screenshots (the uploader is in English);
// their captions, legends and accessible names are translated.
import type { WebUploaderCopy } from "./copy.en";
import en from "./copy.en";

const fr: WebUploaderCopy = {
  seo: {
    title: "L'envoi web de Photohawk | Plus rapide que le FTP",
    description:
      "Nous avons repensé l'outil d'envoi web de Photohawk. Il envoie de nombreuses photos à la fois, gère des envois de plus de 100\u00a0000 photos et reprend tout seul si votre connexion coupe, sur toutes les offres. Sur toutes les offres, des aperçus légers partent en premier pour que votre galerie soit en ligne en quelques minutes, pas en quelques heures.",
  },
  hero: {
    badge: "Nouveau",
    eyebrow: "Sur toutes les offres",
    title: "Le nouvel outil d'envoi web de Photohawk",
    subtitle:
      "Plus rapide que le FTP, conçu pour des envois de plus de 100\u00a0000 photos, et il reprend là où il s'était arrêté.",
    plans: "Sur toutes les offres, les aperçus d'abord mettent un grand événement en ligne en quelques minutes, pas en quelques heures.",
    ctaPrimary: "Ouvrir l'outil d'envoi web",
    ctaSecondary: "Voir les nouveautés",
    screenshotAlt:
      "L'outil d'envoi web en plein envoi. Avec les aperçus d'abord (toutes les offres), la barre d'état indique que la galerie est en ligne avec les aperçus et combien de personnes la consultent, et la file d'attente à droite montre les aperçus terminés et les originaux en route, avec deux photos achetées qui passent en premier.",
    phoneAlt: "L'outil d'envoi web sur un téléphone, ouvert dans un dossier de la galerie, avec l'état de l'envoi en bas.",
  },
  previews: {
    heading: "Les aperçus d'abord",
    badge: "Toutes les offres",
    subtitle:
      "Sur toutes les offres\u00a0: en ligne en quelques minutes, pas en quelques heures. Une galerie de 10\u00a0000 photos peut être en ligne et vendre en environ 8 minutes avec un débit montant de 20 Mbps.",
    body: "Des aperçus légers partent en premier\u00a0: votre galerie peut ainsi être en ligne et commencer à vendre, ou à proposer des téléchargements gratuits, pendant que les originaux suivent. Si quelqu'un achète ou télécharge une photo avant l'arrivée de son original, cet original passe en tête de la file.",
    steps: [
      { id: "previews", text: "Les aperçus légers partent en premier" },
      { id: "live", text: "Votre galerie est en ligne" },
      { id: "buy", text: "Quelqu'un achète une photo" },
      { id: "jump", text: "Son original passe en tête de la file" },
      { id: "originals", text: "Les originaux suivent" },
    ],
    anim: {
      ...en.previews.anim,
      label: "Illustration animée\u00a0: les aperçus d'abord",
      description:
        "Les aperçus légers de six photos volent de votre ordinateur vers votre galerie, et la galerie passe à «\u00a0Live\u00a0». Quelqu'un appuie sur «\u00a0Buy\u00a0» sur une photo\u00a0: son original passe en tête de la file des originaux et part en premier. Les autres originaux suivent.",
    },
    link: "Comment fonctionnent les envois avec aperçus d'abord",
  },
  faster: {
    heading: "Pourquoi il est plus rapide que le FTP",
    subtitle: "Les mêmes photos, envoyées de deux façons.",
    anim: {
      ...en.faster.anim,
      label: "Comparaison animée d'un logiciel FTP et de l'outil d'envoi web",
      description:
        "Deux rangées. En haut, un logiciel FTP envoie les photos 2 à la fois par défaut, et 10 au maximum. En bas, l'outil d'envoi web envoie de nombreuses photos à la fois, sur des voies qui s'élargissent et se resserrent selon la connexion. Sur toutes les offres, les aperçus légers partent en premier, si bien que sa galerie est en ligne plus tôt. La connexion coupe un instant et l'envoi reprend tout seul.",
      legendPreviews: "Aperçus",
      legendOriginals: "Originaux",
    },
    points: [
      {
        id: "parallel",
        title: "De nombreuses photos à la fois",
        body: "Il envoie de nombreuses photos à la fois, bien plus qu'un logiciel FTP comme FileZilla (2 à la fois par défaut, et 10 au maximum).",
      },
      {
        id: "adaptive",
        title: "Tire pleinement parti de votre connexion",
        body: "Il s'ajuste en continu pendant l'envoi, pour tirer pleinement parti de votre connexion.",
      },
      {
        id: "scale",
        title: "Conçu pour les grands événements",
        body: "Nous l'avons testé à grande échelle\u00a0: il gère sans difficulté des envois de plus de 100\u00a0000 photos.",
      },
      {
        id: "live",
        title: "En ligne en quelques minutes, pas en quelques heures (toutes les offres)",
        body: "Sur toutes les offres, des aperçus légers partent en premier\u00a0: une galerie de 10\u00a0000 photos peut ainsi être en ligne et vendre en environ 8 minutes avec un débit montant de 20 Mbps, pendant que les originaux suivent.",
      },
      {
        id: "resume",
        title: "Reprend là où il s'était arrêté",
        body: "Si votre connexion coupe, il reprend tout seul. Ajoutez à nouveau le dossier\u00a0: seul ce qui manque est envoyé.",
      },
    ],
  },
  /** The upload-time estimator (#estimate). {placeholders} are filled in by the page. */
  estimate: {
    heading: "Combien de temps prendra votre envoi\u00a0?",
    subtitle: "Une estimation pour votre galerie, à partir de votre propre débit montant.",
    heroLink: "Estimer la durée de votre envoi",
    photos: "Nombre de photos",
    custom: "Autre",
    customLabel: "Nombre de photos (autre)",
    size: "Taille moyenne d'un original",
    sizeUnit: "Mo",
    sizeHint: "Les JPEG d'événements font souvent environ {mb}\u00a0Mo.",
    speed: "Votre débit montant",
    speedUnit: "Mbps",
    test: "Tester mon débit montant",
    testing: "Test en cours… {mbps} Mbps",
    testStarting: "Test en cours…",
    testDone: "Mesuré\u00a0: {mbps} Mbps en envoi",
    testFailed: "Nous n'avons pas pu mesurer votre débit pour le moment. Saisissez-le vous-même, ou faites un test de débit général.",
    dataNote: "Utilise jusqu'à environ {mb}\u00a0Mo de données, et seulement quand vous appuyez sur le bouton.",
    otherTest: "Ou utiliser un test de débit général",
    resultsHeading: "Votre estimation",
    previewsLabel: "Les aperçus d'abord",
    previewsPlans: "Toutes les offres",
    previewsLive: "Galerie en ligne en {time}",
    previewsAll: "Tous les originaux envoyés en {time}",
    originalsLabel: "Originaux seulement",
    originalsLive: "Galerie en ligne en {time}",
    originalsWhen: "une fois les {gb}\u00a0Go d'originaux envoyés",
    computerNote: "À ce débit, c'est la vitesse à laquelle votre ordinateur crée les aperçus qui donne le rythme.",
    disclaimer: "Ce ne sont que des estimations. Les durées réelles dépendent de votre connexion, de votre ordinateur et de vos photos.",
    time: {
      under: "moins d'une minute",
      min: "environ {n}\u00a0min",
      h: "environ {n}\u00a0h",
      d: "environ {n}\u00a0jours",
    },
    table: {
      caption: "Exemple\u00a0: 10\u00a0000 photos",
      speed: "Débit montant",
      live: "Les aperçus d'abord\u00a0: galerie en ligne (toutes les offres)",
      all: "Tous les originaux envoyés",
      note: "Le chiffre d'environ 8 minutes a été mesuré avec un débit montant de 20 Mbps (aperçus d'abord). Les autres lignes sont des estimations issues du même modèle, avec des originaux d'environ {mb}\u00a0Mo.",
    },
  },
  features: {
    heading: "Encore du nouveau",
    subtitle: "De vraies captures du nouvel outil d'envoi web.",
    items: [
      {
        id: "status",
        title: "L'état, en termes simples",
        body: "Voyez d'un coup d'œil où en est l'envoi. Avec les aperçus d'abord (toutes les offres)\u00a0: «\u00a0Previews\u00a0» (aperçus), puis «\u00a0Originals\u00a0» (originaux), chacun avec sa propre progression.",
        alt: "L'état de l'envoi avec les aperçus d'abord (toutes les offres)\u00a0: «\u00a0Live — saving originals…\u00a0», une ligne de progression pour «\u00a0Previews\u00a0» et une pour «\u00a0Originals\u00a0», et «\u00a02 photos people bought go first\u00a0».",
      },
      {
        id: "queue",
        title: "Une file d'attente qui se place où vous voulez",
        body: "Ancrez la file d'attente à droite, à gauche ou en bas, et faites-la glisser à la taille qui vous convient. Votre navigateur retient votre choix.",
        alt: "La file d'attente ancrée en bas de l'outil d'envoi, avec son menu ouvert pour la placer à droite, en bas ou à gauche.",
      },
      {
        id: "visitors",
        title: "Voyez qui regarde",
        body: "La barre d'état indique combien de personnes consultent votre galerie en ce moment, sur toutes les offres.",
        alt: "La barre d'état en haut de l'outil d'envoi\u00a0: «\u00a0Live on previews\u00a0» (avec les aperçus d'abord) et le nombre de visiteurs.",
      },
      {
        id: "resume",
        title: "Reprend là où il s'était arrêté",
        body: "Si votre connexion coupe, il reprend tout seul. Ajoutez à nouveau le dossier\u00a0: seul ce qui manque est envoyé.",
        alt: "Un bandeau dans l'outil d'envoi indiquant qu'un dossier n'a pas fini d'être envoyé, avec un bouton «\u00a0Resume\u00a0».",
      },
      {
        id: "phone",
        title: "Clair aussi sur téléphone",
        body: "Sur un petit écran, le chemin des dossiers se replie pour tenir, et un appui sur «\u00a0…\u00a0» affiche les dossiers intermédiaires. Vous savez toujours où vont vos photos.",
        alt: "L'outil d'envoi sur un téléphone\u00a0: le chemin affiche le dossier actuel, et «\u00a0…\u00a0» ouvre la liste des dossiers au-dessus.",
      },
    ],
  },
  ftp: {
    date: "1er novembre 2026",
    heading: "Ce qui change pour le FTP le 1er novembre",
    body: "Nos serveurs FTP partagés, actifs en permanence, ferment le 1er novembre 2026. Vous utilisez le FTP aujourd'hui\u00a0? Merci de changer avant cette date.",
    points: [
      {
        id: "closes",
        title: "Le FTP partagé ferme le 1er novembre 2026",
        body: "Les serveurs FTP partagés, actifs en permanence, s'arrêtent le 1er novembre 2026.",
      },
      {
        id: "standard",
        title: "L'outil d'envoi web devient la façon standard d'envoyer vos photos",
        body: "Il est inclus dans toutes les offres, et vous pouvez l'utiliser dès aujourd'hui.",
      },
      {
        id: "ondemand",
        title: "Si le FTP vous est indispensable",
        body: "Sur toutes les offres, chaque membre de votre espace de travail peut démarrer son propre serveur FTP à la demande depuis l'outil d'envoi web, à une adresse personnelle qui ne change jamais. Il s'arrête après une heure sans activité.",
      },
    ],
    helpLink: "Comment fonctionne le FTP à la demande",
  },
  more: {
    previewFirstTitle: "Comment fonctionnent les envois avec aperçus d'abord",
    previewFirstBody: "Sur toutes les offres. Étape par étape\u00a0: les aperçus, la mise en ligne et ce que voient les acheteurs.",
  },
  cta: {
    heading: "Essayez-le lors de votre prochain envoi",
    body: "Le nouvel outil d'envoi web est prêt sur toutes les offres. Ajoutez un dossier et regardez votre galerie passer en ligne.",
    button: "Ouvrir l'outil d'envoi web",
  },
  pause: "Mettre l'animation en pause",
  play: "Lire l'animation",
};

export default fr;
